<script setup lang="ts">
// ================================
// IMPORTS
// ================================

// Vue ecosystem
import { computed } from "vue"

// Types
import type { Source } from "@types"

// Stores
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"

// Store instantiations
const sourceStore = useSourceStore()
const authStore = useAuthStore()

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<Props>()

// ================================
// COMPUTED PROPERTIES (données depuis le store)
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Récupère le statut de la déclaration de droit d'accès GRDF
 */
const getStatusDda = computed(() => {
    return getSourceById.value?.informations.pce?.droit_access?.etat
})

/**
 * Récupère le nom complet du propriétaire du compteur
 */
const getFullNameOwner = computed(() => {
    const firstName =
        getSourceById.value?.informations.pce?.consentement?.titulaire?.user
            ?.firstname || ""
    const lastName =
        getSourceById.value?.informations.pce?.consentement?.titulaire?.user
            ?.lastname || ""
    return `${firstName} ${lastName}`.trim()
})

/**
 * Récupère la date de début de récupération des données
 */
const getDateFromData = computed(() => {
    const date =
        getSourceById.value?.informations.pce?.droit_access
            ?.date_premiere_donnee
    return date ?? null
})

/**
 * Récupère la date de dernière modification du consentement
 */
const getDateLastConsent = computed(() => {
    const date = getSourceById.value?.informations.pce?.consentement?.date_to
    return date ?? null
})

/**
 * Indique si l'utilisateur connecté est le propriétaire du compteur
 */
const isOwner = computed(() => {
    const ownerEmail =
        getSourceById.value?.informations.pce?.consentement?.titulaire?.user
            ?.username
    const currentUserEmail = authStore.username
    return ownerEmail === currentUserEmail
})

/**
 * Récupère la raison de l'erreur
 */
const getReason = computed(() => {
    return getSourceById.value?.informations.pce?.droit_access?.raison ?? ""
})

/**
 * Récupère le code d'erreur
 */
const getCodeError = computed(() => {
    return getSourceById.value?.informations.pce?.droit_access?.code_erreur
})

/**
 * Met à jour le store quand la machine a récupéré une source (émit par le contenu).
 */
function onSourceFetched(source: Source) {
    sourceStore.upsertSource(source)
}

/**
 * Props passées au composant de contenu (données métier pour messages et actions)
 */
const contentProps = computed(() => ({
    meterId: props.meterId,
    idConsent:
        getSourceById.value?.informations.pce?.consentement?._id_consentement ||
        "",
    zipcode:
        getSourceById.value?.informations.pce?.droit_access
            ?.code_postal_utilise || "",
    dateFromData: getDateFromData.value,
    dateLastConsent: getDateLastConsent.value,
    status: getStatusDda.value ?? "",
    reason: getReason.value || null,
    codeError: getCodeError.value ?? null,
    fullNameOwner: getFullNameOwner.value,
    ownerUsername:
        getSourceById.value?.informations.pce?.consentement?.titulaire?.user
            ?.username || "",
    isOwner: isOwner.value,
}))
</script>

<template>
    <GrdfDdaMessageErrorContent
        v-bind="contentProps"
        @source-fetched="onSourceFetched"
    />
</template>
